import { Chip, Skeleton, useTheme } from '@mui/material';
import dinero from 'dinero.js';
import useSextforceMetricsCrossReferenceMetricEarnings from '../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import { d2f } from '../../../../utils/common';

type Props = {
    metricId: string;
    type: 'campaign' | 'trial';
    crossReferenceSubscriberId: string;
    displayMode: 'gross' | 'net';
    addAmount?: number;
};

const SextforceCrossReferenceEarningsLabel = (props: Props) => {
    const { metricId, type, crossReferenceSubscriberId, displayMode, addAmount = 0 } = props;
    const theme = useTheme();

    const { data: sextforceMetricsCrossReferenceMetricEarnings, isLoading: sextforceMetricsCrossReferenceMetricEarningsLoading } =
        useSextforceMetricsCrossReferenceMetricEarnings(
            metricId,
            type,
            crossReferenceSubscriberId === 'none' ? '' : crossReferenceSubscriberId,
        );

    if (crossReferenceSubscriberId === 'none' || !sextforceMetricsCrossReferenceMetricEarnings) {
        return null;
    }

    if (sextforceMetricsCrossReferenceMetricEarningsLoading) {
        return <Skeleton width={'100%'} height={30} />;
    }

    // Base earnings (gross or net) as a float — the "success" portion, on the right.
    const baseAmount = d2f(
        displayMode === 'gross'
            ? sextforceMetricsCrossReferenceMetricEarnings.total
            : sextforceMetricsCrossReferenceMetricEarnings.totalNet,
    );

    // addAmount is the "secondary" portion, on the left.
    const combinedAmount = baseAmount + addAmount;
    const isInactive = baseAmount === 0 && addAmount === 0;

    const crossColor = theme.palette.secondary.main;
    const mainColor = theme.palette.success.main;

    // Share of the combined value that comes from addAmount (secondary / left).
    // Guard against divide-by-zero and clamp to [0, 100].
    const secondaryPercent = combinedAmount !== 0 ? Math.min(100, Math.max(0, (addAmount / combinedAmount) * 100)) : 50;

    // When both amounts are zero, render an inactive grey chip.
    // Otherwise: secondary (addAmount) on the left up to secondaryPercent, success (base earnings) on the right.
    const background = isInactive
        ? theme.palette.action.disabledBackground
        : `linear-gradient(to right, ${mainColor} 0%, ${mainColor} ${secondaryPercent}%, ${crossColor} ${secondaryPercent}%, ${crossColor} 100%)`;

    return (
        <Chip
            label={dinero({
                amount: Math.trunc(combinedAmount * 100),
                currency: 'USD',
            }).toFormat()}
            sx={{
                width: '100%',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                justifyContent: 'flex-end',
                color: isInactive ? theme.palette.text.disabled : theme.palette.common.white,
                background,
                '& .MuiChip-label': {
                    textShadow: isInactive ? 'none' : '0 0 3px rgba(0, 0, 0, 0.6)',
                },
            }}
        />
    );
};

export default SextforceCrossReferenceEarningsLabel;
